import { useEffect, useState } from "react";

import {
  flattenConversations,
  useArchiveConversation,
  useConversations,
} from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useRenameChat } from "~/ai-chat/feature/use-rename-chat";
import type { ConversationGroupBy, ConversationStatusFilter } from "~/ai-chat/model/conversation";
import { ConversationList } from "~/ai-chat/ui/conversation-list";

/**
 * Chat-history sidebar: lists the user's conversations, selects one, and starts a
 * fresh chat. Loading the selected conversation into the transcript is handled by
 * `useHydrateActiveConversation` (mounted in the page/widget shell) so switching
 * works even with this list closed.
 */
export function AiChatHistorySidebar({ onAfterSelect }: { onAfterSelect?: () => void }) {
  const {
    activeConversationId,
    setActiveConversationId,
    setChatTitle,
    startNewConversation,
    markConversationSynced,
    runningConversationIds,
  } = useAiChat();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ConversationStatusFilter>("active");
  const [groupBy, setGroupBy] = useState<ConversationGroupBy>("date");
  // Debounce so a search doesn't fire (and cache) a query per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // "active" hides archived; "all" includes them. (Archived-only needs a backend flag.)
  const conversationsQuery = useConversations({ q: debouncedSearch, includeArchived: statusFilter === "all" });
  const { rename } = useRenameChat();
  const archiveMutation = useArchiveConversation();

  const conversations = flattenConversations(conversationsQuery.data);

  const select = (id: string) => {
    // Switching away replaces the local transcript, so the departing
    // conversation's unsynced protection no longer guards anything real —
    // without releasing it, returning to a conversation whose persistence
    // failed would show the *other* conversation's messages under its id.
    if (activeConversationId !== null && activeConversationId !== id) {
      markConversationSynced(activeConversationId);
    }
    setActiveConversationId(id);
    setChatTitle(conversations.find((conversation) => conversation.id === id)?.title ?? null);
    onAfterSelect?.();
  };

  const startNew = () => {
    startNewConversation();
    onAfterSelect?.();
  };

  return (
    <ConversationList
      conversations={conversations}
      activeId={activeConversationId}
      onSelect={select}
      onNew={startNew}
      onRename={rename}
      onArchive={(id, archived) => archiveMutation.mutate({ conversationId: id, archived })}
      search={search}
      onSearchChange={setSearch}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      onLoadMore={() => void conversationsQuery.fetchNextPage()}
      hasMore={conversationsQuery.hasNextPage}
      isFetchingMore={conversationsQuery.isFetchingNextPage}
      isLoading={conversationsQuery.isLoading}
      runningIds={runningConversationIds}
    />
  );
}
